<script>
import { computed } from 'vue';

import { useRouter } from 'vue-router';

import BmMetricDescriptionBox from '@/components/MetricDescriptionBox/MetricDescriptionBox.vue';

import useFilterState from '@/state/useFilterState';
import useUserState from '@/state/useUserState';

import { showProductTour } from '@/utils/productTour';

import {
  track,
} from '@/utils/analytics';

import {
  capitalize,
  joinUnderscore,
} from '@/utils/stringHelpers';

import navigate from '@/utils/navigate';
import formatValue from '@/utils/formatValue';

import {
  numericCurrFormat,
  numericFormat,
  percentFormat,
} from '@/config/valueFormatConfig';

import {
  OPPORTUNITIES_HINTS,
} from '@/modules/productTour';

import i18n from '@/config/i18n';

import {
  getHcOpportunitiesLos,
  getHcOpportunitiesWebsiteTraffic,
  getHcOpportunitiesBeConversion,
  getHcOpportunitiesBeToBookingConversionRate,
  getHcOpportunitiesAbv,
  getHcOpportunitiesAdr,
  getHcOpportunitiesDisparity,
} from '@/config/hcLinks';

export default {
  components: {
    BmMetricDescriptionBox,
  },
  props: {
    opp: {
      type: Object,
      default: () => {},
    },
    isPropertySummarySection: {
      type: Boolean,
    },
  },
  setup(props) {
    const router = useRouter();

    const {
      setFilteredOtas,
      setSource,
      setDevices,
      setTravelParty,
      setGeolocations,
      clearAllSavedFilters,
      setOpportunitiesMetric,
    } = useFilterState();
    const { showUpselling } = useUserState();

    const { t } = i18n.global;

    const VISITOR_PROFILE_URL = '/visitor-profile';
    const DISPARITIES_URL = '/disparities';
    const CONVERSION_URL = '/conversion';
    const REVENUE_URL = '/revenue';
    const LOS_URL = '/lenght-of-stay';
    const OPPORTUNITIES_URL = '/opportunities';

    const parseValue = (type, value) => {
      switch (type) {
        case 'number':
          return formatValue(value, numericFormat);
        case 'currency':
          return formatValue(value, numericCurrFormat);
        case 'percentage':
          return formatValue(value, percentFormat);
        default:
          return value;
      }
    };

    const oppTitle = computed(() => {
      if (props.opp.isDisparity) {
        return `${t(props.opp.title, { metric: props.opp.metric })} ${t('for {strataValue}', { strataValue: t(props.opp.ota) })}`;
      }
      if (props.opp.strataValue === 'all') {
        return `${t(props.opp.title, { metric: props.opp.metric })}`;
      }
      return `${t(props.opp.title, { metric: props.opp.metric })} ${t('for {strataValue}', { strataValue: t(capitalize(props.opp.strataValue)) })}`;
    });

    const hideStrataType = computed(() => props.opp.strataType === 'total' && props.opp.strataValue === 'all');

    const tpConfig = computed(() => ({
      type: 'info',
      content: {
        text: t(joinUnderscore(props.opp.description), {
          metric: props.opp.metric,
          strataType: hideStrataType.value ? '' : t(capitalize(props.opp.strataType)).toLowerCase(),
          strataValue:
            props.opp.strataType === 'country' ? t(capitalize(props.opp.strataValue)) : t(capitalize(props.opp.strataValue)).toLowerCase(),
          uplift: props.opp.impact === 'max' ? t('Over 100%') : props.opp.uplift,
          propertyValue: parseValue(props.opp.valueType, props.opp.propertyValue),
          compsetValue: parseValue(props.opp.valueType, props.opp.compsetValue),
        }),
      },
      preferred: ['right'],
    }));

    const handleOpportunityClick = () => {
      if (props.isPropertySummarySection) {
        track('click - summary opportunity');
        router.push(OPPORTUNITIES_URL);
      }
    };

    const handleLinkClick = async () => {
      await clearAllSavedFilters();

      if (props.opp.isDisparity) {
        await router.push(DISPARITIES_URL);
        setFilteredOtas([capitalize(props.opp.ota)]);
      } else {
        switch (props.opp.rawMetric) {
          case 'length of stay':
            await router.push(LOS_URL);
            break;
          case 'visits':
            await router.push(VISITOR_PROFILE_URL);
            break;
          case 'visit to search conversion rate':
            setOpportunitiesMetric('searchConversion');
            await router.push(CONVERSION_URL);
            break;
          case 'search to booking conversion rate':
            setOpportunitiesMetric('beConversion');
            await router.push(CONVERSION_URL);
            break;
          case 'avg booking value':
            await router.push(REVENUE_URL);
            break;
          case 'avg daily revenue':
            setOpportunitiesMetric('adrDailyRate');
            await router.push(REVENUE_URL);
            break;
          default:
            await router.push(CONVERSION_URL);
        }
        if (props.opp.strataValue && props.opp.strataType) {
          switch (props.opp.strataType) {
            case 'device':
              setDevices([props.opp.strataValue]);
              break;
            case 'country':
              setGeolocations([props.opp.rawStrataValue]);
              break;
            case 'source':
              setSource([props.opp.strataValue]);
              break;
            case 'travel party':
              setTravelParty([props.opp.strataValue]);
              break;
            default:
              break;
          }
        }
      }
    };

    const handleHcClick = () => {
      track('click - HC opportunities');
      if (!showUpselling.value) {
        switch (props.opp.rawMetric) {
          case 'length of stay':
            navigate(getHcOpportunitiesLos(), { external: true, newTab: true });
            break;
          case 'visits':
            navigate(getHcOpportunitiesWebsiteTraffic(), { external: true, newTab: true });
            break;
          case 'visit to search conversion rate':
            navigate(getHcOpportunitiesBeConversion(), { external: true, newTab: true });
            break;
          case 'search to booking conversion rate':
            navigate(
              getHcOpportunitiesBeToBookingConversionRate(),
              { external: true, newTab: true },
            );
            break;
          case 'avg booking value':
            navigate(getHcOpportunitiesAbv(), { external: true, newTab: true });
            break;
          case 'avg daily revenue':
            navigate(getHcOpportunitiesAdr(), { external: true, newTab: true });
            break;
          default:
            navigate(getHcOpportunitiesDisparity(), { external: true, newTab: true });
        }
      } else {
        showProductTour(OPPORTUNITIES_HINTS);
      }
    };

    return {
      oppTitle,
      tpConfig,
      hideStrataType,
      showUpselling,
      capitalize,
      joinUnderscore,
      handleOpportunityClick,
      handleLinkClick,
      handleHcClick,
      parseValue,
    };
  },
};
</script>

<template>
  <LpCard
    :class="[
      'opportunityCard overflow-hidden',
      { 'opportunityCard--summary card-highlight': isPropertySummarySection },
    ]"
    @click="handleOpportunityClick()"
  >
    <div class="p-6 opportunityCard__body">
      <div
        :class="[
          'opportunityCard__body--propertyBody',
          { 'flex-wrap': isPropertySummarySection },
        ]"
      >
        <div>
          <LpTag
            :class="[
              'inline-flex mr-4 whitespace-nowrap',
              opp.impact + '-level',
            ]"
            size="medium"
          >
            <span class="text-sm text-base-900">
              {{ $t(capitalize(opp.impact === 'max' ? 'High' : opp.impact) + ' impact') }}
            </span>
          </LpTag>
          <LpTag
            v-if="isPropertySummarySection"
            class="inline-flex mb-2 mr-4"
            size="medium"
            color="gray"
          >
            <span class="text-sm text-base-900">
              {{ $t(capitalize(opp.goal)) }}
            </span>
          </LpTag>
        </div>
        <div class="w-full">
          <bmMetricDescriptionBox
            :metric="oppTitle"
            :tp-config="isPropertySummarySection ? {} : tpConfig"
            :class="{ 'mt-4 text-lg font-medium': isPropertySummarySection }"
            @click="handleLinkClick()"
          />
        </div>
        <div class="text-right hcCta min-w-options-list">
          <LpButton
            data-testid="helpCta"
            mode="outline"
            color="gray"
            size="small"
            @click="handleHcClick()"
          >
            {{ showUpselling ? $t('Show hints') : $t('Strategies to act now') }}
          </LpButton>
        </div>
      </div>
    </div>

    <div
      v-if="!isPropertySummarySection"
      class="opportunityCard__footer"
    >
      <div v-if="opp.goal">
        <span class="opportunityCard__footer--name"> {{ $t('Goal') }} </span>
        <span class="opportunityCard__footer--value"> {{ $t(capitalize(opp.goal)) }} </span>
      </div>
      <div v-if="opp.strataType || opp.strataValue">
        <span class="opportunityCard__footer--name"> {{ $t('Stratum') }} </span>
        <span class="opportunityCard__footer--value">
          <span v-if="hideStrataType">
            {{ $t(capitalize(opp.strataValue)) }}
          </span>
          <span v-else>
            {{ ($t(capitalize(opp.strataType))) + ', ' + $t(capitalize(opp.strataValue)) }}
          </span>
        </span>
      </div>
      <div v-if="opp.ota && opp.isDisparity">
        <span class="opportunityCard__footer--name"> {{ $t('OTA') }} </span>
        <span class="opportunityCard__footer--value"> {{ $t(opp.ota) }} </span>
      </div>
      <div v-if="opp.uplift && opp.isDisparity">
        <span class="opportunityCard__footer--name"> {{ $t('Estimated drop') }} </span>
        <span class="opportunityCard__footer--value"> -{{ opp.uplift }} </span>
      </div>
      <div v-else-if="opp.uplift">
        <span class="opportunityCard__footer--name"> {{ $t('Estimated Uplift') }} </span>
        <span class="opportunityCard__footer--value"> {{ (opp.impact === 'max' ? $t('Over 100%') : '+' + opp.uplift) }} </span>
      </div>
      <div v-if="opp.lostRevenue">
        <span
          v-if="opp.isDisparity"
          class="opportunityCard__footer--name"
        > {{ $t('Est. Dropped Disparities') }} </span>
        <span
          v-else
          class="opportunityCard__footer--name"
        > {{ $t('Estimated Lost Revenue') }} </span>
        <div>
          <LpTag
            color="primary"
            size="medium"
          >
            <span class="opportunityCard__footer--value"> {{ (opp.impact === 'max' ? $t('Over') + ' ' + opp.lostRevenue : opp.lostRevenue) }} </span>
          </LpTag>
        </div>
      </div>
    </div>
  </LpCard>
</template>

<style scoped>
  .opportunityCard--summary {
    @apply cursor-pointer inline-block;
    flex: 45%;
    .hcCta {
      @apply hidden;
    }
  }
  .opportunityCard__body {
    > * + * {
      @apply mt-6;
    }
    &--propertyBody {
      @apply flex relative gap-2 justify-between;
    }
  }
  .opportunityCard__footer {
    @apply flex p-6 bg-base-100 gap-8;
    > * {
      @apply flex flex-col gap-2;
    }
    &--name {
      @apply text-base-700 text-sm;
    }
    &--value {
      @apply text-base-900 text-lg py-0.5 whitespace-nowrap;
      &:first-letter {
        @apply uppercase;
      }
    }
  }
  :deep(.metric-text) {
    @apply cursor-pointer;
    &:hover {
      strong {
        @apply underline;
      }
    }
  }
  .lp-tag {
    @apply mb-2;
    &.high-level,
    &.max-level {
      @apply bg-danger-300;
    }
    &.medium-level {
      @apply bg-warning-300;
    }
    &.low-level {
      @apply bg-success-300;
    }
  }
  .arrow-icon {
    @apply transform -rotate-45;
  }
</style>
